<template>
  <m-input
    v-model="textValue"
    class="date-input"
    :label="label"
    :sr-label="srLabel"
    :placeholder="placeholder"
    :rules="rules"
    :required-text="requiredText || `Enter a date for '${srLabel || label}'`"
    :horizontal="horizontal"
    :label-width="labelWidth"
    :show-all-errors="showAllErrors"
    :input-format="dateMask"
    :large="large"
    :name="name"
    :helper="helper"
    :instructions="instructions"
    :actions="actions"
    :actions-left="actionsLeft"
    @blur="onBlur"
    @focus="onFocus"
    @keydown.capture="onKeydown"
  >
    <template v-if="$slots.helper" #helper> <slot name="helper" /></template>

    <template #append>
      <m-button
        ref="calendarButton"
        icon="fa-light fa-calendar"
        icon-only
        ghost
        small
        color="secondary"
        aria-haspopup="dialog"
        :aria-expanded="showForm"
        :aria-controls="id"
        @click="showForm = !showForm"
      >
        <span class="sr-only">Open date picker for {{ srLabel || splitByUpperCase(name) }}</span>
        <q-popup-proxy v-model="showForm" transition-show="scale" transition-hide="scale">
          <q-date
            ref="datePickerEl"
            v-model="input"
            minimal
            mask="DD/MM/YYYY"
            :navigation-max-year-month="maxDateNavigation"
            :navigation-min-year-month="minDateNavigation"
            :options="dateOptions"
            bordered
            @update:model-value="showForm = false"
            @navigation="onNavigationHandler()"
          >
            <m-button
              ref="datePickerCloseButton"
              v-close-popup
              icon="fa-solid fa-xmark"
              icon-only
              color="secondary"
              sr-label="Close"
              :outline="false"
              :small="true"
              :dense="true"
              class="borderless"
              @click="showForm = false"
            />
          </q-date>
        </q-popup-proxy>
      </m-button>
    </template>
  </m-input>
</template>

<script lang="ts" setup>
import { QDate, QPopupProxy } from "quasar";
import MButton from "../../MButton";
import MInput from "./../MInput";

import { date } from "quasar";
import { computed, nextTick, provide, ref, watch, onBeforeMount } from "vue";
import { useDateValue } from "../../../../composables/dateValue";
import { addOrdinalSuffix, formatDateTimeMedicus } from "../../../../utils/date";
import { landmarkDateMenu } from "../../../../utils/dialog";
import { getMaxDate, getMinDate, randomID } from "../../../../utils/helpers";
import { splitByUpperCase } from "../../../../utils/string";

const props = defineProps({
  modelValue: {
    type: [Object, String, Number, Array, Date],
    required: false,
  },
  name: String,
  label: String,
  srLabel: String,
  rules: Array as () => Array<(v: any) => boolean | string>,
  showAllErrors: Boolean,
  // m-labelled text props
  horizontal: {
    type: Boolean,
    default: undefined,
  },
  labelWidth: String,

  placeholder: {
    type: String,
    default: "dd/mm/yyyy",
  },

  minDate: {
    type: [Date, Number, String],
    default() {
      return new Date(1000, 1, 1);
    },
  },
  maxDate: {
    type: [Date, Number, String],
    default() {
      return new Date(new Date().getFullYear() + 100, 1, 1);
    },
  },

  requiredText: String,

  minDateText: {
    type: String,
  },
  maxDateText: {
    type: String,
  },

  large: Boolean,

  helper: String,
  instructions: String,

  masked: Boolean,

  actions: Array,
  actionsLeft: Boolean,
});

onBeforeMount(() => {
  if (!props.label && !props.srLabel) {
    // TODO, this is temporary as checkLabel does not work anymore
    console.warn("Either label or srLabel must be provided");

    // eslint-disable-next-line no-undef
    if (IS_APP) {
      // more info needed to actually find it on the page. DEV only.
      console.warn(props);
    }
    // Optionally throw error:
    // throw new Error('Either "label" or "srLabel" prop is required.');
  }
});

provide("input-dont-use-name", true);

const showForm = ref(false);
const id = randomID("data-picker");

const { dateText, rules, input, displayDate } = useDateValue(
  props,
  false,
  undefined,
  props.rules,
  () => props.requiredText || `Enter a date for '${props.label}'`,
);

const textValue = ref(displayDate.value);
const shouldMask = ref(false);
const calendarButton = ref<typeof MButton>(null);

const dateMask = computed(() => (shouldMask.value ? "dd/mm/yyyy" : undefined));
const maxDateNavigation = computed(() => {
  const d = getMaxDate(props.maxDate, false);
  return `${d.getFullYear()}/${(d.getMonth() + 1).toString().padStart(2, "0")}`;
});
const minDateNavigation = computed(() => {
  const d = getMinDate(props.minDate, false);
  return `${d.getFullYear()}/${(d.getMonth() + 1).toString().padStart(2, "0")}`;
});

function dateOptions(dateValue: any) {
  const d = new Date(dateValue);
  const max = getMaxDate(props.maxDate, false);
  const min = getMinDate(props.minDate, false);

  return date.isBetweenDates(d, min, max, {
    inclusiveFrom: true,
    inclusiveTo: true,
    onlyDate: true,
  });
}

function parseDate(d: string, splitter: string) {
  let [day, month, year, ...rest] = d.split(splitter);

  if (!day || !month || !year || rest?.length) {
    return undefined;
  }

  if (year && +year < 100) {
    year = "20" + year.padStart(2, "0");
  }

  if (month && +month < 10) {
    month = month.padStart(2, "0");
  }

  if (day && +day < 10) {
    day = day.padStart(2, "0");
  }

  return [day, month, year].join("/");
}

const tokenRegex = /-?(\d+)([d|w|m|y])/g;

function parseToken(t: string) {
  const matches = t.match(tokenRegex);
  if (!matches) {
    return;
  }

  let d = new Date();

  for (const t of matches) {
    const v = +t.slice(0, -1);

    switch (t[t.length - 1]) {
      case "d": {
        d = date.addToDate(d, { days: v });
        break;
      }
      case "w": {
        d = date.addToDate(d, { days: v * 7 });
        break;
      }
      case "m": {
        d = date.addToDate(d, { months: v });
        break;
      }
      case "y": {
        d = date.addToDate(d, { years: v });
        break;
      }
    }
  }
  return formatDateTimeMedicus(d);
}

function onKeydown(e: KeyboardEvent) {
  if (props.masked) return;

  switch (e.key?.toLowerCase()) {
    case "-":
    case "d":
    case "w":
    case "m":
    case "y":
      shouldMask.value = false;
      e.stopImmediatePropagation();
      break;
    case "/":
      shouldMask.value = true;
      break;
    default: {
      if (e.code.startsWith("Key") && !e.altKey && !e.shiftKey && !e.ctrlKey) {
        e.preventDefault();
      }
    }
  }
}

function onFocus() {
  textValue.value = dateText.value;
  nextTick(() => {
    // shouldMask.value = true;
    shouldMask.value = !!textValue.value;
  });
}

async function onBlur(e?: Event) {
  try {
    if (e && (e.target as HTMLElement).classList.contains("m-input--input-box")) {
      e.preventFormTouch = true;
      return;
    }
    shouldMask.value = false;
    if (props.masked) {
      dateText.value = textValue.value;
      return;
    }

    let text = textValue.value;
    if (!text) {
      // if the text is empty
      input.value = "";
      return;
    }

    // if the text is a number and has 6 or 8 digits (14022024 or 140224)
    if (text.match(/^(\d{6}|\d{8})$/)) {
      text = `${text.slice(0, 2)}/${text.slice(2, 4)}/${text.slice(4)}`;
    }

    let actualDate = parseDate(text, "/");
    if (!actualDate) {
      actualDate = parseDate(text, ".");
      if (!actualDate) {
        actualDate = parseToken(text.toLowerCase());
        if (!actualDate) {
          // invalid date
          return;
        }
      }
    }
    input.value = actualDate.split(" ")[0].split("-").reverse().join("/");
  } finally {
    shouldMask.value = false;
    await nextTick();
    textValue.value = displayDate.value;
  }
}

watch(input, () => {
  nextTick(calendarButton.value.focus);
});

watch(displayDate, (t, p) => {
  if (textValue.value === p) {
    textValue.value = t;
  }
});

watch(textValue, (t) => {
  // slash cannot be the first character
  shouldMask.value = t?.indexOf("/") > 0;
});

watch(showForm, async (v) => {
  if (v) {
    await nextTick();
    landmarkDateMenu(datePickerEl.value.$el, "q-portal--menu");
  }
});

const datePickerEl = ref(null as QDate | null);
const datePickerCloseButton = ref<MButton>(null);

const onNavigationHandler = () => {
  setTimeout(() => updateCalendarHTML(), 333);
};

watch(datePickerEl, async () => {
  if (datePickerEl.value) {
    await nextTick();
    updateCalendarHTML();
  }
});

const updateCalendarHTML = (): void => {
  // ENG-15172, WCAG. Avoiding updating Quasar library by manipulating the date picker HTML
  // Also trying to avoid the need for a Table
  const dayMap = {
    Mon: "Monday",
    Tue: "Tuesday",
    Wed: "Wednesday",
    Thu: "Thursday",
    Fri: "Friday",
    Sat: "Saturday",
    Sun: "Sunday",
  };

  const el = datePickerEl.value?.$el as HTMLElement | null;
  // ENG-15172, WCAG landmark corrections
  if (el) {
    const parent = el.parentElement;
    parent.role = "dialog";
    parent.id = id;
    parent.ariaLabel = "Date picker";
  }

  const keys = Object.keys(dayMap);
  const header = datePickerEl.value?.$el.querySelector(".q-date__navigation");
  const closeButton = datePickerCloseButton.value.$el;

  header.appendChild(closeButton);

  const daysOfTheWeek = datePickerEl.value.$el.querySelectorAll(
    ".q-date__calendar-weekdays .q-date__calendar-item",
  );
  const daysOfTheMonth = datePickerEl.value.$el.querySelectorAll(
    ".q-date__calendar-days .q-date__calendar-item",
  );

  const buttons = datePickerEl.value?.$el.querySelectorAll("button.q-btn");
  buttons[0].ariaLabel = "Go to previous month";
  buttons[1].ariaLabel = `Change the current month: ${buttons[1].textContent.trim()}`;
  buttons[1].ariaHasPopup = "dialog";
  buttons[1].ariaExpanded = "false";
  buttons[2].ariaLabel = "Go to next month";
  buttons[3].ariaLabel = "Go to previous year";
  buttons[4].ariaLabel = `Change the current year: ${buttons[4].textContent.trim()}`;
  buttons[4].ariaHasPopup = "dialog";
  buttons[4].ariaExpanded = "false";
  buttons[5].ariaLabel = "Go to next year";

  // adds full day of the week.
  daysOfTheWeek.forEach((item) => {
    const div = item.querySelector("div");
    if (!div) return;
    div.ariaHidden = "true";
    const abbr = div.textContent.trim();
    const full = dayMap[abbr];

    if (full) {
      if (!item.querySelector(".sr-only")) {
        const sr = document.createElement("div");
        sr.className = "sr-only";
        sr.textContent = full;
        item.appendChild(sr);
      }
    }
  });

  daysOfTheMonth.forEach((day, i) => {
    const key = keys[i % 7]; // cycle through the keys
    const full = dayMap[key];
    day?.querySelector("div")?.setAttribute("aria-hidden", "true");
    const button = day?.querySelector("button") || null;
    const dayNumber = day?.querySelector(".q-btn__content") || null;
    if (dayNumber) {
      dayNumber.ariaHidden = "true";
    }
    const dayDate = addOrdinalSuffix(day.textContent.trim());
    const sr = document.createElement("span");
    sr.className = "sr-only";
    sr.textContent = `${full} ${dayDate}`;
    if (!day.querySelector(".sr-only")) {
      button?.appendChild(sr);
    }
  });
};
</script>
<style lang="scss">
.date-input {
  .m-input--input-box {
    max-width: 11em;
  }
}
.q-date__calendar-weekdays > div {
  opacity: 0.62;
}
.q-date__actions:empty {
  display: none;
}
</style>
